import { useState } from 'react';
import axios from 'axios';
import { Calendar, ExternalLink, Loader2, Video, Clock, User, MapPin, Phone, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { formatBackendDate } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';
import OnboardingPremiumCard from './OnboardingPremiumCard';

const API = API_BASE;

function formatSlotDateTime(iso) {
  if (!iso) return 'Date to be confirmed';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return formatBackendDate(iso);
  }
}

function formatSlotDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function formatSlotTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

// Plain-language copy shared with the applicant dashboard tests. Interview must
// never read like a wall the applicant has to wait behind.
export const INTERVIEW_KEEP_GOING_MESSAGE = 'You can keep completing your application before your interview.';
export const INTERVIEW_TO_BE_ARRANGED_TITLE = 'Interview to be arranged';
export const INTERVIEW_NO_WAIT_MESSAGE = 'You do not need to wait — continue with the tasks below.';

// Pure, testable presentation of an interview appointment. Keeps the JSX simple
// and lets tests assert on date/time/method without rendering the whole card.
export function getInterviewPresentation(appointment) {
  const status = String(appointment?.status || '').toLowerCase();
  const hasAppointment = Boolean(appointment)
    && ['scheduled', 'confirmed'].includes(status)
    && appointment?.is_past !== true;
  const scheduledAt = appointment?.scheduled_at || appointment?.starts_at || null;
  const dateText = scheduledAt ? formatSlotDate(scheduledAt) : '';
  const timeText = scheduledAt ? formatSlotTime(scheduledAt) : '';
  const timezone = appointment?.timezone || appointment?.time_zone || '';
  const methodRaw = String(appointment?.method || appointment?.method_label || '').toLowerCase();
  const isVideo = methodRaw.includes('video');
  const isPhone = methodRaw.includes('phone') || methodRaw.includes('tele');
  const isInPerson = methodRaw.includes('person') || methodRaw.includes('office') || methodRaw.includes('site');
  const methodText = appointment?.method_label
    || (isVideo ? 'Video call' : isPhone ? 'Telephone call' : isInPerson ? 'In person' : (appointment?.method || 'Interview'));
  const locationText = appointment?.location || appointment?.address || '';
  const statusLabel = status === 'confirmed'
    ? 'Confirmed'
    : status === 'scheduled'
      ? 'Scheduled'
      : (appointment?.status || '');
  return {
    hasAppointment,
    status,
    scheduledAt,
    dateText,
    timeText,
    timezone,
    methodText,
    locationText,
    isVideo,
    isPhone,
    isInPerson,
    statusLabel,
  };
}

export default function InterviewAppointmentCard({
  interviewForm,
  appointment,
  bookingSlots = [],
  interviewState = null,
  onRefresh,
  onOpenQuestionnaire,
  onGoToTask,
  isFormWorkerActionRequired,
  getFormActionLabel,
  getFormActionButtonClass,
  appointmentOnly = false,
}) {
  // Worker portal stores its JWT under 'workerToken' (NOT the admin 'token'
  // used by useAuth). Reading it here matches every other worker feature in
  // WorkerDashboard.js — using useAuth() left this null and silently no-op'd
  // Book/Cancel.
  const token = localStorage.getItem('workerToken');
  const [bookingId, setBookingId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const stateKey = interviewState?.state || null;
  // C3F: an active appointment only counts as "scheduled" when it is genuinely
  // upcoming — a past active appointment projects to "awaiting outcome", never
  // an Upcoming card.
  const isUpcoming = appointment
    && ['scheduled', 'confirmed'].includes(String(appointment.status || '').toLowerCase())
    && appointment.is_past !== true;
  const questionnaireNeedsAction = interviewForm && isFormWorkerActionRequired?.(interviewForm.status);
  const hasAppointment = isUpcoming;
  const presentation = getInterviewPresentation(appointment);
  // C3F #6: booking is only offered when the canonical state says so (the same
  // prerequisite truth the recruitment journey uses), never merely because
  // stale slots exist.
  const stateAllowsBooking = interviewState ? stateKey === 'choose_slot' : true;
  const canBook = !appointmentOnly && !hasAppointment && (bookingSlots || []).length > 0
    && interviewForm
    && !questionnaireNeedsAction
    && stateAllowsBooking;
  // Terminal / review / awaiting states: show a truthful status, never a
  // passive "wait for an email" message or a stale "choose a date" step.
  const statusOnlyState = ['under_review', 'awaiting_outcome', 'successful', 'unsuccessful'].includes(stateKey);

  const handleBook = async (slotId) => {
    if (!token || !slotId) return;
    setBookingId(slotId);
    try {
      await axios.post(
        `${API}/worker/interview-appointment/book`,
        { slot_id: slotId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Interview slot booked');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not book this slot');
    } finally {
      setBookingId(null);
    }
  };

  const handleCancel = async () => {
    if (!token || !appointment?.id || !appointment.can_cancel) return;
    setCancelling(true);
    try {
      await axios.delete(`${API}/worker/interview-appointment/${appointment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Interview booking cancelled');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <OnboardingPremiumCard
      tone="purple"
      data-testid={appointmentOnly ? 'interview-appointment-surface' : 'interview-step'}
      title={interviewState?.headline
        || (appointmentOnly ? 'Upcoming interview' : 'Interview')}
      description={interviewState?.subtext
        || (appointmentOnly
          ? 'Your interview has been scheduled by your care provider. Details are below.'
          : 'Complete your questionnaire, then attend your video interview to verify original documents.')}
      icon={User}
    >
      {interviewForm && !statusOnlyState ? (
        <div
          className={`flex items-center justify-between rounded-xl border p-4 shadow-sm transition-colors ${
            questionnaireNeedsAction ? 'cursor-pointer hover:border-primary/20 hover:bg-slate-50/80 border-purple-100' : 'border-purple-100 bg-white'
          }`}
          onClick={questionnaireNeedsAction ? () => onOpenQuestionnaire?.(interviewForm) : undefined}
          data-testid={`form-${interviewForm.id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100 shrink-0">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-800">{interviewForm.name}</span>
                <Badge className="bg-purple-100 text-purple-700 text-xs">Before interview</Badge>
              </div>
              {!questionnaireNeedsAction ? (
                <p className="text-xs text-purple-700 mt-0.5">Submitted — recruitment can schedule your interview</p>
              ) : null}
            </div>
          </div>
          {questionnaireNeedsAction ? (
            <Button size="sm" className={getFormActionButtonClass?.(interviewForm)}>
              {getFormActionLabel?.(interviewForm)}
            </Button>
          ) : (
            <Badge className="bg-blue-100 text-blue-700 shrink-0">Done</Badge>
          )}
        </div>
      ) : null}

      {hasAppointment ? (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50/80 p-4" data-testid="interview-appointment-scheduled">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-green-900">Your interview</p>
                {presentation.statusLabel ? (
                  <Badge className="bg-green-100 text-green-800 text-xs" data-testid="interview-appointment-status">
                    {presentation.statusLabel}
                  </Badge>
                ) : null}
              </div>
              <p className="mt-1.5 flex items-center gap-2 text-base font-semibold text-green-900" data-testid="interview-appointment-date">
                <Calendar className="h-4 w-4 shrink-0" />
                {presentation.dateText || formatSlotDateTime(presentation.scheduledAt)}
              </p>
              <p className="mt-1 flex items-center gap-2 text-sm text-green-800" data-testid="interview-appointment-time">
                <Clock className="h-4 w-4 shrink-0" />
                {presentation.timeText || 'Time to be confirmed'}
                {presentation.timezone ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700">
                    <Globe className="h-3 w-3" />
                    {presentation.timezone}
                  </span>
                ) : null}
              </p>
              <p className="mt-1 flex items-center gap-2 text-sm text-green-800" data-testid="interview-appointment-method">
                {presentation.isVideo ? <Video className="h-4 w-4 shrink-0" />
                  : presentation.isPhone ? <Phone className="h-4 w-4 shrink-0" />
                    : <MapPin className="h-4 w-4 shrink-0" />}
                {presentation.methodText}
                {appointment.interviewer_name ? ` · with ${appointment.interviewer_name}` : ''}
              </p>
              {presentation.locationText ? (
                <p className="mt-1 flex items-center gap-2 text-sm text-green-800" data-testid="interview-appointment-location">
                  <MapPin className="h-4 w-4 shrink-0" />
                  {presentation.locationText}
                </p>
              ) : null}
              {appointment.source === 'interview_record' ? (
                <p className="mt-1 text-xs text-green-700">Arranged by the recruitment team</p>
              ) : null}
              {!appointment.video_meeting_link && presentation.isVideo ? (
                <p className="mt-2 text-xs text-amber-800" data-testid="interview-video-pending-note">
                  Video link pending — your care provider will add the joining link before your interview.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {appointment.video_meeting_link ? (
                <Button
                  size="sm"
                  className="bg-green-700 hover:bg-green-800 text-white"
                  asChild
                >
                  <a href={appointment.video_meeting_link} target="_blank" rel="noopener noreferrer" data-testid="join-video-interview">
                    <Video className="h-4 w-4 mr-1" />
                    Join video call
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
              ) : presentation.isVideo ? (
                <Badge className="bg-amber-100 text-amber-800" data-testid="interview-video-pending">Video link pending</Badge>
              ) : null}
              {appointment.can_cancel ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="border-green-300 text-green-800"
                  data-testid="cancel-interview-btn"
                >
                  {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change or cancel'}
                </Button>
              ) : null}
            </div>
          </div>
          <p className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-sm font-medium text-green-900" data-testid="interview-keep-going">
            {INTERVIEW_KEEP_GOING_MESSAGE}
          </p>
        </div>
      ) : canBook ? (
        <div className="mt-4 space-y-2" data-testid="interview-slot-picker">
          <p className="text-sm font-medium text-slate-800">Book an interview slot</p>
          <p className="text-xs text-slate-600">Choose a time that works for you. You will verify original documents during the call.</p>
          <ul className="space-y-2">
            {bookingSlots.map((slot) => (
              <li
                key={slot.id}
                className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{formatSlotDateTime(slot.starts_at)}</p>
                  <p className="text-xs text-slate-600 flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    {slot.duration_minutes || 30} minutes · {slot.method_label || 'Video call'}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleBook(slot.id)}
                  disabled={bookingId === slot.id}
                  data-testid={`book-slot-${slot.id}`}
                >
                  {bookingId === slot.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Book this slot'}
                </Button>
              </li>
            ))}
          </ul>
          <p className="mt-2 rounded-lg bg-purple-50 px-3 py-2 text-sm font-medium text-purple-900" data-testid="interview-keep-going">
            {INTERVIEW_KEEP_GOING_MESSAGE}
          </p>
        </div>
      ) : stateKey === 'uploads_required' ? (
        <div className="mt-4 space-y-3 rounded-xl border border-purple-200 bg-purple-50 p-4" data-testid="interview-uploads-required">
          <p className="text-sm font-semibold text-purple-900">Finish these items to choose a date</p>
          <p className="text-xs text-purple-800">You only need to submit your documents here. Our team will check them separately, so you won't be waiting for admin approval to book.</p>
          <ul className="space-y-2">
            {(interviewState?.missing_items || []).map((item) => (
              <li key={item.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-purple-100 bg-white p-3">
                <span className="text-sm text-slate-800">{item.reason}</span>
                {item.route && onGoToTask && (
                  <Button size="sm" variant="outline" onClick={() => onGoToTask(item)} data-testid={`interview-missing-${item.key}`}>
                    Complete
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <p className="text-xs text-purple-800">Already uploaded something? Refresh your dashboard to update this checklist.</p>
        </div>
      ) : statusOnlyState ? null : (
        <div className="mt-3 rounded-xl border border-purple-100 bg-purple-50/60 p-4" data-testid="interview-to-be-arranged">
          <p className="text-sm font-semibold text-purple-900">{INTERVIEW_TO_BE_ARRANGED_TITLE}</p>
          {questionnaireNeedsAction ? (
            <p className="mt-1 text-sm text-purple-800">
              Complete your pre-interview questionnaire above. You can choose your interview time once availability opens, or the recruitment team will arrange it with you.
            </p>
          ) : (
            <p className="mt-1 text-sm text-purple-800">
              The recruitment team will arrange your interview time with you.
            </p>
          )}
          <p className="mt-2 text-sm font-medium text-purple-900" data-testid="interview-no-wait">
            {INTERVIEW_NO_WAIT_MESSAGE}
          </p>
        </div>
      )}
    </OnboardingPremiumCard>
  );
}